import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "../components/ui";

const SUPPORT_EMAIL = "support@maxandsherry.mw";

const faqs = [
  {
    q: "How do I create an account?",
    a: "Download the Max & Sherry ConnectPlus app, open it, and follow the sign-up steps. After signing up, add your photo, occupation, and bio so people can get to know you.",
  },
  {
    q: "How do I reset or recover my account?",
    a: "Log in with the same method you used when you registered. If you still cannot get in, email us from the address linked to your account and we will help you recover access.",
  },
  {
    q: "How do I update my profile?",
    a: "Open the Profile section in the app to change your photo, occupation, and bio, or to adjust your preferences such as age range, location, and interests.",
  },
  {
    q: "How do I report or block another user?",
    a: "Open the person's profile or your chat with them in the app and choose Report or Block. For serious concerns, email safety@maxandsherry.mw or visit our Safety Centre.",
  },
  {
    q: "How do I delete my account?",
    a: "Use the Delete account page on this website. Enter your account email, then the 6-digit code we send you. If you change your mind, log back in before the scheduled date to cancel the deletion.",
  },
  {
    q: "What if I have trouble with payments or subscriptions?",
    a: "Check your payment history on your Account page. If a payment failed, confirm your mobile money balance and number, then try again. If you were charged but your plan did not activate, email billing@maxandsherry.mw with your account email and the transaction reference.",
  },
];

const links = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms and Conditions" },
  { to: "/delete-account", label: "Account deletion instructions" },
  { to: "/safety", label: "Safety Centre" },
];

export default function Support() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const canSend =
    name.trim() !== "" && /\S+@\S+\.\S+/.test(email) && message.trim() !== "";

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSend) return;
    const subject = `Support request from ${name}`;
    const body = `${message}\n\nName: ${name}\nAccount email: ${email}`;
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
  }

  const inputClass =
    "w-full px-4 py-3.5 rounded-2xl border border-black/10 text-sm outline-none focus:border-rose transition-colors duration-200";

  return (
    <main className="bg-white px-[5%] pt-[140px] pb-20 lg:pb-[100px]">
      <div className="max-w-[1100px] mx-auto">
        <Reveal>
          <Eyebrow>Support</Eyebrow>
        </Reveal>
        <Reveal delay={100}>
          <h1 className="text-[clamp(28px,4vw,44px)] font-bold tracking-[-1.2px] mb-4">
            Max &amp; Sherry ConnectPlus Support
          </h1>
        </Reveal>
        <Reveal delay={150}>
          <p className="text-[15px] text-muted mb-12 max-w-[600px] leading-relaxed">
            Max &amp; Sherry ConnectPlus is a dating and connection app for
            Malawi, built for real connections based on values, interests, and
            vibe. Need help, want to report a problem, or have a question? We
            are here for you.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
          {/* ---------- Contact support ---------- */}
          <Reveal delay={200}>
            <div className="p-6 lg:p-8 rounded-[24px] bg-blush border border-rose/10">
              <h2 className="text-lg font-bold tracking-tight mb-2">
                Contact support
              </h2>
              <p className="text-[14px] text-muted leading-relaxed mb-4">
                Email us at{" "}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-rose-dark font-medium"
                >
                  {SUPPORT_EMAIL}
                </a>
                . We aim to respond within 24 to 48 hours. Support hours are
                Monday to Friday, 8:00 AM to 5:00 PM (CAT).
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  aria-label="Your name"
                  className={inputClass}
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your account email"
                  aria-label="Your account email"
                  className={inputClass}
                />
                <textarea
                  required
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe your issue"
                  aria-label="Describe your issue"
                  className={`${inputClass} resize-none`}
                />
                <button
                  type="submit"
                  disabled={!canSend}
                  className="w-full bg-black text-white text-sm font-semibold py-3.5 rounded-full hover:bg-neutral-800 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Send message
                </button>
                <p className="text-[12px] text-muted text-center">
                  This opens your email app with your message ready to send.
                </p>
              </form>
            </div>
          </Reveal>

          {/* ---------- FAQs ---------- */}
          <Reveal delay={250}>
            <div>
              <h2 className="text-lg font-bold tracking-tight mb-4">
                Frequently asked questions
              </h2>
              <div className="space-y-3">
                {faqs.map((f, i) => {
                  const isOpen = openIndex === i;
                  return (
                    <div
                      key={f.q}
                      className="rounded-2xl border border-black/10 bg-white overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenIndex(isOpen ? null : i)}
                        aria-expanded={isOpen}
                        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 text-[14px] font-semibold text-black"
                      >
                        <span>{f.q}</span>
                        <span className="text-rose text-lg leading-none flex-shrink-0">
                          {isOpen ? "−" : "+"}
                        </span>
                      </button>
                      {isOpen && (
                        <p className="px-5 pb-5 text-[14px] text-muted leading-relaxed">
                          {f.a}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </div>

        {/* ---------- Useful links ---------- */}
        <Reveal delay={300}>
          <div className="mt-12 p-6 lg:p-8 rounded-[24px] border border-black/10">
            <h2 className="text-lg font-bold tracking-tight mb-4">
              Useful links
            </h2>
            <div className="flex flex-wrap gap-3">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="bg-white border border-black/10 text-black text-sm font-medium px-5 py-2.5 rounded-full hover:border-rose hover:text-rose transition-colors duration-200 no-underline"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
